import dayjs from '~/timeInit';

import { isValidDateTimeInput } from '../dateTime';
import { isKnownValue } from '../parseHelpers';
import { isSiteRegionCode } from '../regions';
import type { PayloadValidationResult } from '../types';
import { isHttpsUrl, isInternalRoute } from '../url';
import { validateRequiredString } from '../validationHelpers';

import { type WhatsNewCarouselFieldLabelKey, fieldLabel } from './fieldLabels';
import type { WhatsNewCarouselCard, WhatsNewCarouselPayloadBuilderState } from './types';
import { WHATS_NEW_CAROUSEL_IMAGE_FIT_OPTIONS } from './types';

export type WhatsNewCarouselValidationResult = PayloadValidationResult;

function validationLabel(sectionLabel: string, field: WhatsNewCarouselFieldLabelKey): string {
  return `${sectionLabel} ${fieldLabel(field)}`;
}

function validateEnabledRegions(regions: Array<string>, label: string, errors: Array<string>) {
  const invalidRegions = regions.filter((region) => !isSiteRegionCode(region));
  if (invalidRegions.length > 0) {
    errors.push(`${validationLabel(label, 'enabledRegions')} has invalid codes: ${invalidRegions.join(', ')}`);
  }
}

function validateCtaUrl(value: string, label: string, errors: Array<string>) {
  const ctaUrlLabel = validationLabel(label, 'ctaUrl');
  validateRequiredString(value, ctaUrlLabel, errors);
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return;
  }
  if (isHttpsUrl(trimmedValue) || isInternalRoute(trimmedValue)) {
    return;
  }
  errors.push(`${ctaUrlLabel} must be either an https:// URL or an internal route starting with /`);
}

function validateDateRange(startAt: string, endAt: string, label: string, errors: Array<string>) {
  const isStartAtValid = isValidDateTimeInput(startAt);
  const isEndAtValid = isValidDateTimeInput(endAt);

  if (!isStartAtValid) {
    errors.push(`${validationLabel(label, 'startAt')} must be a valid datetime`);
  }
  if (!isEndAtValid) {
    errors.push(`${validationLabel(label, 'endAt')} must be a valid datetime`);
  }
  if (!isStartAtValid || !isEndAtValid) {
    return;
  }

  if (dayjs(endAt).isBefore(dayjs(startAt))) {
    errors.push(`${validationLabel(label, 'endAt')} must be the same as or after ${fieldLabel('startAt')}`);
  }
}

function validateImageOptionsFit(value: WhatsNewCarouselCard['imageOptionsFit'], label: string, errors: Array<string>) {
  if (!value) {
    return;
  }

  if (!isKnownValue(value, WHATS_NEW_CAROUSEL_IMAGE_FIT_OPTIONS)) {
    errors.push(
      `${validationLabel(label, 'imageOptionsFit')} must be ${WHATS_NEW_CAROUSEL_IMAGE_FIT_OPTIONS.join(' or ')}`,
    );
  }
}

function validateCard(card: WhatsNewCarouselCard, errors: Array<string>) {
  const label = card.draftLabel;
  validateEnabledRegions(card.enabledRegions, label, errors);
  validateDateRange(card.startAt, card.endAt, label, errors);
  validateRequiredString(card.imageId, validationLabel(label, 'imageId'), errors);
  validateImageOptionsFit(card.imageOptionsFit, label, errors);
  validateRequiredString(card.overline, validationLabel(label, 'overline'), errors);
  validateRequiredString(card.title, validationLabel(label, 'title'), errors);
  validateRequiredString(card.description, validationLabel(label, 'description'), errors);
  validateRequiredString(card.ctaLabel, validationLabel(label, 'ctaLabel'), errors);
  validateCtaUrl(card.ctaUrl, label, errors);
}

export function validateWhatsNewCarouselPayload(
  state: WhatsNewCarouselPayloadBuilderState,
): WhatsNewCarouselValidationResult {
  const errors: Array<string> = [];

  if (state.cards.length === 0) {
    errors.push('Cards requires at least one card');
  }

  state.cards.forEach((card) => validateCard(card, errors));

  return {
    isValid: errors.length === 0,
    errors,
  };
}
